import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { ArrowRight, Sparkles, Send, Mail, Wand2 } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { RefundQueueCard } from "@/components/admin/sponsored/RefundQueueCard";

function timeAgo(iso) {
  if (!iso) return "—";
  const s = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
  if (s < 60) return `${s}s ago`;
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
}

export default function AdminOverview() {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [recent, setRecent] = useState([]);
  const [digest, setDigest] = useState(null);
  const [sending, setSending] = useState(false);
  const [aiAdoption, setAiAdoption] = useState(null);
  const [emailStatus, setEmailStatus] = useState(null);
  const [testEmailBusy, setTestEmailBusy] = useState(false);

  useEffect(() => {
    api.get("/admin/stats").then((r) => setStats(r.data)).catch(() => setStats(null));
    api.get("/admin/onboarding").then((r) => setRecent(r.data.slice(0, 5))).catch(() => setRecent([]));
    api.get("/admin/weekly-digest").then((r) => setDigest(r.data)).catch(() => setDigest(null));
    api.get("/admin/ai-coupons/analytics?days=30")
      .then((r) => setAiAdoption(r.data))
      .catch(() => setAiAdoption(null));
    api.get("/admin/email/status")
      .then((r) => setEmailStatus(r.data))
      .catch(() => setEmailStatus(null));
  }, []);

  const testDomainSender = async () => {
    if (!user?.email) return;
    setTestEmailBusy(true);
    try {
      const { data } = await api.post("/admin/email/test-send", {
        to: user.email,
        override_sender: "hello@cyannect.co",
      });
      if (data.ok) {
        toast.success(`Sent from hello@cyannect.co — check ${user.email}`);
      } else {
        toast.error(`Domain not verified yet: ${data.error || data.provider}`);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Test send failed");
    } finally {
      setTestEmailBusy(false);
    }
  };

  const sendDigest = async () => {
    setSending(true);
    try {
      await api.post("/admin/weekly-digest/send");
      toast.success("Digest sent to master admins");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Only super admins can send the digest.");
    } finally {
      setSending(false);
    }
  };

  if (!stats) return <div className="text-slate-500">Loading...</div>;

  const cards = [
    { label: "Users", value: stats.total_users, chip: "pf-chip-cyan" },
    { label: "Merchants", value: stats.total_merchants, chip: "pf-chip-lav" },
    { label: "Coupons", value: stats.total_coupons, chip: "pf-chip-mint" },
    { label: "Total clicks", value: stats.total_clicks, chip: "pf-chip-cyan" },
    { label: "Pending brands", value: stats.pending_stores, chip: "pf-chip-lav", highlight: stats.pending_stores > 0 },
    { label: "Pending coupons", value: stats.pending_coupons, chip: "pf-chip-mint", highlight: stats.pending_coupons > 0 },
  ];

  return (
    <div data-testid="admin-overview">
      <div className="pf-chip pf-chip-cyan">Console</div>
      <h2 className="font-display text-3xl md:text-4xl font-medium tracking-tight mt-3 text-slate-900">
        Welcome, <span className="pf-gradient-text-2">{user?.name || "Admin"}.</span>
      </h2>
      <p className="text-sm text-slate-600 mt-1">The state of Cyannect at a glance.</p>

      <RefundQueueCard />

      <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mt-6">
        {cards.map((c, i) => (
          <div key={i} className={`pf-card p-6 ${c.highlight ? "ring-2 ring-cyan-400/60" : ""}`}>
            <span className={`pf-chip ${c.chip} w-fit`}>{c.label}</span>
            <div className="font-display text-4xl font-medium mt-4 text-slate-900 tabular-nums">{c.value}</div>
          </div>
        ))}
      </div>

      {/* Weekly digest preview */}
      <div className="mt-6 pf-card p-6 bg-gradient-to-br from-cyan-50/60 to-emerald-50/60">
        <div className="flex items-start justify-between flex-wrap gap-3">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-2xl bg-gradient-to-br from-cyan-500 to-emerald-500 flex items-center justify-center">
              <Mail className="w-5 h-5 text-white" />
            </div>
            <div>
              <div className="font-display text-xl font-medium text-slate-900">Weekly digest</div>
              <div className="text-xs text-slate-500 mt-0.5">Auto-delivered every Monday 09:00 UTC to all master admins</div>
            </div>
          </div>
          {user?.role === "super_admin" && (
            <div className="flex items-center gap-2">
              <Link to="/admin/digest-log" className="pf-btn-glass px-4 py-2 rounded-full text-xs font-semibold" data-testid="view-digest-log">
                Digest log
              </Link>
              <button onClick={sendDigest} disabled={sending} className="pf-btn-primary px-4 py-2 rounded-full text-xs font-semibold flex items-center gap-2" data-testid="send-digest">
                <Send className="w-3.5 h-3.5" /> {sending ? "Sending…" : "Send now"}
              </button>
            </div>
          )}
        </div>
        {digest && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-5">
            <div className="rounded-2xl bg-white/60 p-4">
              <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">New brands</div>
              <div className="font-display text-3xl text-slate-900 tabular-nums mt-1">{digest.new_merchants}</div>
            </div>
            <div className="rounded-2xl bg-white/60 p-4">
              <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">New coupons</div>
              <div className="font-display text-3xl text-slate-900 tabular-nums mt-1">{digest.new_coupons}</div>
            </div>
            <div className="rounded-2xl bg-white/60 p-4">
              <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">Clicks · 7d</div>
              <div className="font-display text-3xl text-slate-900 tabular-nums mt-1">{digest.clicks_last_7}</div>
            </div>
            <div className="rounded-2xl bg-white/60 p-4">
              <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">Pending approvals</div>
              <div className="font-display text-3xl text-slate-900 tabular-nums mt-1">{digest.pending_stores + digest.pending_coupons}</div>
            </div>
          </div>
        )}
      </div>

      {/* AI Coupon Adoption */}
      {aiAdoption && (
        <div className="mt-6 pf-card p-6" data-testid="ai-adoption-card">
          <div className="flex items-start justify-between flex-wrap gap-3">
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-2xl bg-gradient-to-br from-cyan-500 to-emerald-500 flex items-center justify-center">
                <Wand2 className="w-5 h-5 text-white" />
              </div>
              <div>
                <div className="font-display text-xl font-medium text-slate-900">AI coupon adoption</div>
                <div className="text-xs text-slate-500 mt-0.5">
                  Last {aiAdoption.window_days} days · manual vs AI-assisted coupon creation
                </div>
              </div>
            </div>
            <div className="pf-chip pf-chip-cyan">
              {aiAdoption.totals.ai_share_pct}% created with AI
            </div>
          </div>

          {/* Share bar */}
          <div className="mt-5">
            <div className="flex items-center justify-between text-xs text-slate-600 mb-1.5">
              <span data-testid="ai-adoption-ai-count">
                AI · <span className="font-mono text-slate-900">{aiAdoption.totals.ai_published}</span>
              </span>
              <span data-testid="ai-adoption-manual-count">
                Manual · <span className="font-mono text-slate-900">{aiAdoption.totals.manual}</span>
              </span>
            </div>
            <div className="h-2.5 rounded-full bg-slate-100 overflow-hidden flex">
              <div className="h-full bg-gradient-to-r from-cyan-500 to-emerald-500"
                   style={{ width: `${aiAdoption.totals.ai_share_pct}%` }}
                   data-testid="ai-adoption-share-bar" />
              <div className="h-full bg-slate-400/70"
                   style={{ width: `${aiAdoption.totals.manual_share_pct}%` }} />
            </div>
          </div>

          {/* Funnel + rates */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-5">
            <div className="rounded-2xl bg-white/60 p-4">
              <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">Sessions opened</div>
              <div className="font-display text-3xl text-slate-900 tabular-nums mt-1" data-testid="ai-adoption-opened">
                {aiAdoption.ai_funnel.sessions_opened}
              </div>
            </div>
            <div className="rounded-2xl bg-white/60 p-4">
              <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">Completion rate</div>
              <div className="font-display text-3xl text-slate-900 tabular-nums mt-1" data-testid="ai-adoption-completion">
                {aiAdoption.rates.completion_rate_pct}%
              </div>
              <div className="text-[10px] text-slate-500 mt-1">Ready-to-publish drafts</div>
            </div>
            <div className="rounded-2xl bg-white/60 p-4">
              <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">Draft → publish</div>
              <div className="font-display text-3xl text-slate-900 tabular-nums mt-1" data-testid="ai-adoption-publish-rate">
                {aiAdoption.rates.draft_to_publish_pct}%
              </div>
              <div className="text-[10px] text-slate-500 mt-1">Of ready drafts published</div>
            </div>
            <div className="rounded-2xl bg-white/60 p-4">
              <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">Handed off manual</div>
              <div className="font-display text-3xl text-slate-900 tabular-nums mt-1" data-testid="ai-adoption-handoff">
                {aiAdoption.ai_funnel.handed_off_to_manual}
              </div>
              <div className="text-[10px] text-slate-500 mt-1">Chose "Edit manually"</div>
            </div>
          </div>

          {/* 7-day sparkline */}
          {aiAdoption.series_7d?.length > 0 && (
            <div className="mt-5">
              <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold mb-2">Last 7 days</div>
              <div className="flex items-end gap-1.5 h-16" data-testid="ai-adoption-sparkline">
                {aiAdoption.series_7d.map((d) => {
                  const total = d.ai + d.manual;
                  const max = Math.max(1, ...aiAdoption.series_7d.map((x) => x.ai + x.manual));
                  const h = Math.max(4, (total / max) * 60);
                  return (
                    <div key={d.date} className="flex-1 flex flex-col items-center gap-1"
                         title={`${d.date}: AI ${d.ai} · Manual ${d.manual}`}>
                      <div className="w-full rounded-t-sm overflow-hidden flex flex-col" style={{ height: `${h}px` }}>
                        {d.ai > 0 && (
                          <div className="bg-gradient-to-b from-cyan-500 to-emerald-500"
                               style={{ height: `${(d.ai / Math.max(total, 1)) * 100}%` }} />
                        )}
                        {d.manual > 0 && (
                          <div className="bg-slate-400/70 flex-1" />
                        )}
                      </div>
                      <div className="text-[9px] text-slate-500 tabular-nums">{d.date.slice(5)}</div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Email deliverability — sender status + domain-verify test */}
      {emailStatus && (
        <div className="mt-6 pf-card p-6" data-testid="email-deliverability-card">
          <div className="flex items-start justify-between flex-wrap gap-3">
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-2xl bg-gradient-to-br from-cyan-500 to-emerald-500 flex items-center justify-center">
                <Mail className="w-5 h-5 text-white" />
              </div>
              <div>
                <div className="font-display text-xl font-medium text-slate-900">Email deliverability</div>
                <div className="text-xs text-slate-500 mt-0.5">
                  Current sender · <span className="font-mono text-slate-700" data-testid="email-current-sender">{emailStatus.sender}</span>
                </div>
              </div>
            </div>
            <span className={`pf-chip ${emailStatus.on_sandbox ? "pf-chip-lav" : "pf-chip-mint"}`}
                  data-testid="email-status-chip">
              {emailStatus.on_sandbox ? "Resend sandbox" : "Custom domain live"}
            </span>
          </div>

          {emailStatus.on_sandbox && (
            <div className="mt-5 rounded-2xl bg-amber-50/70 border border-amber-200 p-4">
              <div className="font-semibold text-slate-900 text-sm">
                Ready to swap to hello@cyannect.co?
              </div>
              <ol className="mt-2 text-xs text-slate-700 space-y-1 list-decimal list-inside">
                <li>Add the SPF, DKIM and DMARC DNS records from <span className="font-mono">docs/resend-domain-verification.md</span> to your registrar.</li>
                <li>Wait for Resend to mark cyannect.co as "Verified" (usually 15–30 min).</li>
                <li>Tap <b>Send test from hello@cyannect.co</b> below — a successful send proves DNS is live.</li>
                <li>Update <span className="font-mono">SENDER_EMAIL=hello@cyannect.co</span> in backend/.env and restart backend.</li>
              </ol>
              {user?.role === "super_admin" && (
                <button onClick={testDomainSender} disabled={testEmailBusy}
                        className="pf-btn-primary mt-3 px-4 py-2 rounded-full text-xs font-semibold flex items-center gap-2 disabled:opacity-50"
                        data-testid="email-test-send-btn">
                  <Send className="w-3.5 h-3.5" />
                  {testEmailBusy ? "Sending…" : "Send test from hello@cyannect.co"}
                </button>
              )}
            </div>
          )}

          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-5">
            <div className="rounded-2xl bg-white/60 p-4">
              <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">Provider</div>
              <div className="font-display text-xl text-slate-900 mt-1 capitalize">{emailStatus.provider}</div>
            </div>
            <div className="rounded-2xl bg-white/60 p-4">
              <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">Sent · 24h</div>
              <div className="font-display text-3xl text-slate-900 tabular-nums mt-1" data-testid="email-sent-24h">
                {emailStatus.last_24h.sent}
              </div>
            </div>
            <div className="rounded-2xl bg-white/60 p-4">
              <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">Failed · 24h</div>
              <div className={`font-display text-3xl tabular-nums mt-1 ${emailStatus.last_24h.failed > 0 ? "text-rose-600" : "text-slate-900"}`}>
                {emailStatus.last_24h.failed}
              </div>
            </div>
            <div className="rounded-2xl bg-white/60 p-4">
              <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">Mocked · 24h</div>
              <div className="font-display text-3xl text-slate-900 tabular-nums mt-1">{emailStatus.last_24h.mocked}</div>
            </div>
          </div>
        </div>
      )}

      {/* Newest brands onboarding */}
      <div className="mt-10 pf-card p-6">
        <div className="flex items-center justify-between mb-5 flex-wrap gap-2">
          <div className="flex items-center gap-2">
            <Sparkles className="w-4 h-4 text-cyan-600" />
            <div className="font-display text-xl font-medium text-slate-900">Newest brands onboarding</div>
          </div>
          <Link to="/admin/onboarding" className="text-xs font-semibold text-cyan-700 hover:underline flex items-center gap-1" data-testid="overview-view-all-brands">
            View all <ArrowRight className="w-3.5 h-3.5" />
          </Link>
        </div>

        {recent.length === 0 ? (
          <div className="py-8 text-center text-slate-500">No brand signups yet.</div>
        ) : (
          <div className="divide-y divide-white/60">
            {recent.map((r) => (
              <Link
                to="/admin/onboarding"
                key={r.user_id}
                className="flex items-center gap-4 py-4 -mx-2 px-2 rounded-xl hover:bg-white/60 transition-colors"
                data-testid={`overview-brand-${r.user_id}`}
              >
                <img src={r.store?.logo_url || `https://api.dicebear.com/7.x/shapes/svg?seed=${r.email}`} alt="" className="w-10 h-10 rounded-xl border border-white/80 bg-white object-contain" data-logo="1" />
                <div className="flex-1 min-w-0">
                  <div className="font-medium text-slate-900 truncate">{r.store?.name || r.name}</div>
                  <div className="text-xs text-slate-500">{r.email} · joined {timeAgo(r.joined_at)}</div>
                </div>
                <span className={`pf-chip ${r.store_status === "approved" ? "pf-chip-mint" : r.store_status === "pending" ? "pf-chip-lav" : ""}`}>
                  {r.store_status || "—"}
                </span>
                <div className="text-right hidden md:block">
                  <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">Coupons</div>
                  <div className="font-mono text-sm text-slate-900">{r.coupon_total}</div>
                </div>
              </Link>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
